import { useRouter } from 'next/router';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { Season } from '@wbk/api';
import { Button, Modal } from '@wbk/ui';
import { QueueView, useQueue } from '@wbk/queue';
import TabbyIcon from '../svg/Tabby';

type Props = {
  readMore?: boolean;
  backButton?: boolean;
  showTabby?: boolean;
  headingLevel?: 'h1' | 'h2';
  headingText?: string | React.ReactNode;
  tabbyClassName?: string;
  season?: Season;
};

const SeasonText = ({
  headingLevel = 'h1',
  headingText,
  readMore = true,
  backButton,
  showTabby,
  tabbyClassName,
  season,
}: Props) => {
  const { t } = useTranslation();
  const { locale, query } = useRouter();

  const isPrivate = query.private === 'true';

  const { isActive } = useQueue();
  const Heading = headingLevel;

  return (
    <div className='relative z-20 grow space-y-4 pb-8 md:px-4 md:py-8 rtl:pt-14'>
      {backButton && (
        <Button
          as={Link}
          href='/'
          shape='text'
          className='flex w-fit items-center p-1 text-sm'
          replace
          style={{ color: season?.text_color }}
        >
          <svg
            className='shrink-0 ltr:rotate-180'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M8.58984 16.59L13.1698 12L8.58984 7.41L9.99984 6L15.9998 12L9.99984 18L8.58984 16.59Z'
              fill='currentColor'
            />
          </svg>
          <span className='px-1'>{t('tickets:back_to_events')}</span>
        </Button>
      )}
      <Heading className='flex flex-col text-4xl uppercase leading-tight drop-shadow md:text-6xl'>
        {headingText || <span style={{ color: season?.text_color }}>{season?.title}</span>}
      </Heading>

      {season?.sub_title && (
        <p
          className='relative z-10 text-2xl leading-10 drop-shadow md:text-3xl'
          style={{ color: season?.text_color }}
        >
          {season.sub_title}
        </p>
      )}

      {showTabby && (
        <Modal
          size='xl'
          render={({ open }) => (
            <div
              className={`flex items-center gap-2 ${tabbyClassName}`}
              style={{ color: season?.text_color }}
            >
              <p className='text-lg'>
                <Trans i18nKey='tabby_payment_without_price' ns='event'>
                  <button
                    className='font-bold underline hover:text-sky-700'
                    style={{ color: season?.text_color }}
                    onClick={open}
                    aria-label={t('event:tabby_payment_without_price')}
                  ></button>
                </Trans>
              </p>
              <TabbyIcon className='h-6 w-14 shrink-0' />
            </div>
          )}
          body={({ close }) => (
            <div className='relative rounded-lg'>
              <button
                className='absolute top-2 mx-4 flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 p-4 text-lg font-bold text-gray-600 hover:bg-gray-200 focus:bg-gray-200 ltr:right-2 rtl:left-2'
                onPointerUp={close}
              >
                X
              </button>
              <iframe
                title='Tabby'
                className='w-full rounded-lg'
                height='735'
                src={`//widgets.tabby.ai/webviews/product-page/installments/${locale}/?installmentsCount=0`}
              />
            </div>
          )}
        />
      )}

      {isActive ? null : (
        <div className='flex w-full items-center gap-4'>
          <Button
            as={readMore ? Link : process.env.NEXT_PUBLIC_QUEUE === 'true' ? 'a' : Link}
            href={
              isPrivate
                ? `/season/${season?.slug}/book?private=true`
                : `/season/${season?.slug}/book`
            }
            className='w-full max-w-[270px]'
            style={{ background: season?.action_color, color: season?.background_color }}
          >
            <span className='z-10 text-lg font-bold'>
              {readMore ? t('tickets:season_read_more') : t('tickets:buy_now')}
            </span>
          </Button>
        </div>
      )}
      <QueueView style={{ color: season?.text_color }} />
    </div>
  );
};

export default SeasonText;
